/**
 * Custom Element: Discount Table
 * Description: A custom element that displays a discount table for a product.
 * This component requires two attributes: product and bundles.
 * The product attribute is a JSON string that contains the product details.
 * The bundles attribute is a JSON string that contains the discount bundles
 * and the savings for each bundle. The component needs label, 
 * quantity and percentage of discount to work properly.
 */

class DiscountTable extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        // HTML for the component
        this.container = document.createElement('div');
        this.table = document.createElement('table');
    }

    connectedCallback() {
        const style = document.createElement('style')

        this.price = Number(this.getAttribute('price') || 0)
        this.compareAtPrice = Number(this.getAttribute('compare-at-price') || this.price)
        this.bundles = JSON.parse(this.getAttribute('bundles'))

        try {
            this.bundles.forEach(bundle => {
                const formattedPrice = window.Shopify.formatMoney(
                    (this.price - ((this.price * bundle.percentage) / 100)) * bundle.quantity
                )
                const formattedComparePrice = window.Shopify.formatMoney(
                    this.compareAtPrice * bundle.quantity
                )

                const tr = document.createElement('tr')
                const tdLabel = document.createElement('td')
                const tdPrice = document.createElement('td')

                tdLabel.textContent = bundle.label
                tr.appendChild(tdLabel)

                if (this.price > 0) {
                    const spanCompareAtPrice = document.createElement('span')
                    const spanPrice = document.createElement('span')
                    
                    spanCompareAtPrice.className = 'compare-at-price'
                    spanCompareAtPrice.textContent = formattedComparePrice
                    tdPrice.appendChild(spanCompareAtPrice)

                    spanPrice.className = 'price'
                    spanPrice.textContent = formattedPrice
                    tdPrice.appendChild(spanPrice)
                }

                tr.appendChild(tdPrice)

                const tdSave = document.createElement('td')

                tdSave.textContent = `Save ${bundle.percentage}%`

                tr.appendChild(tdSave)

                this.table.appendChild(tr)
            })

            this.table.classList.add('discount-table')
            
            style.textContent = this.generateStyles()

            this.container.appendChild(this.table)
            this.container.className = 'discount-table-container'
            this.shadowRoot.append(style, this.container)

            console.info('√ Custom Discount Table added to the DOM')
        } catch (error) {
            console.error('Error adding Custom Discount Table to the DOM', error);
        }
    }

    generateStyles() {
        return `
            .discount-table-container {
                border-radius: 8px;
                border: 1px solid #483285;
                overflow: hidden;
            }
            .discount-table {
                width: 100%;
                border-collapse: collapse;
                margin: 0;
            }
            .discount-table tr:nth-child(even) {
                background-color: #F8F7F6;
            }
            .discount-table th, 
            .discount-table td {
                padding: 16px 0;
                text-align: center;
            }
            .discount-table th {
                background-color: #f4f4f4;
            }
            .discount-table td {
                width: 33.33%;
                color: #333;
            }
            .discount-table td:first-child,
            .discount-table td:last-child {
                font-family: 'magallanes', sans-serif;
                font-weight: bold;
                line-height: 20px;
                font-size: 14px;
            }
            .discount-table td:last-child {
                color: #399500;
            }
            .discount-table td:nth-child(2) {
                background-color: #EFE9FF;
                font-size: 16px;
                font-weight: 600;
                line-height: 40px;
            }
            .discount-table td:nth-child(2) .compare-at-price {
                color: #696969;
                text-decoration: line-through;
                margin-right: 4px;
            }
            .discount-table .price-column {
                background-color: #E7E7FF;
            }
            .discount-table .save-column {
                background-color: #E0FFE0;
            }
            @media (max-width: 768px) {
                .discount-table td {
                    width: 27.7%;
                }
                .discount-table td:nth-child(2) {
                    width: 44.66%;
                }
            }
        `
    }
}

// Define the new element
customElements.define('discount-table', DiscountTable);